import React, { useCallback, useEffect, useMemo, useState } from "react";
import _ from "lodash";
import cx from "classnames";

import { useSpring, useTransition, animated } from "@react-spring/web";

import { LearningRecordSimulation } from "src/types/coaching/learners";
import WaveSurfer from "wavesurfer.js";

import { SoundVisualizer } from "../visualiser";
import Tooltip from "src/components/tooltip";

import styles from "./player.module.scss";
import { SimulationContext } from "../../context";
import { useDraggable } from "@dnd-kit/core";

interface CallRecordingPlayerProps {
  simulation: LearningRecordSimulation;
  minimized: boolean;
  onPlay?: () => void;
}

export const CallRecordingPlayer: React.FC<CallRecordingPlayerProps> = ({ simulation, minimized, onPlay }) => {
  const simulationContext = React.useContext(SimulationContext);

  const containerRef = React.useRef(null);
  const playerRef = React.useRef<WaveSurfer | null>(null);

  const { url } = useMemo(() => {
    const url = _.get(simulation, 'report.callRecordingUrl');

    return {
      url: url,
    }
  }, [simulation]);

  const [playerState, setPlayerState] = useState({
    muted: false,
    playbackRate: 1,
    volume: 100,
    duration: 0,
    currentTime: 0,
    waveformLoaded: false,
    audioPlaying: false
  });

  const loadingTransition = useTransition(!playerState.waveformLoaded, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const [playerStyles] = useSpring({
    transform: playerState.waveformLoaded ? "translateY(0%)" : "translateY(100%)",
    opacity: playerState.waveformLoaded ? 1 : 0,
  }, [playerState.waveformLoaded]);

  useEffect(() => {
    if (url && WaveSurfer) {
      loadPlayer(url);
    }

    return () => {
      if (playerRef.current) {
        try {
          playerRef.current.destroy();
        } catch (e) {
          console.error("Error destroying WaveSurfer instance: ", e);
        }

        playerRef.current = null;
      }
    };
  }, [url]);

  useEffect(() => {
    if (playerState.audioPlaying && onPlay) {
      onPlay();
    }
  }, [playerState.audioPlaying]);

  const loadPlayer = (url) => {
    const ctx = document.createElement('canvas').getContext('2d')
    const gradient = ctx!.createLinearGradient(0, 0, 0, 150)
    gradient.addColorStop(0, '#FF284F')
    gradient.addColorStop(0.15, '#FF439B')
    gradient.addColorStop(0.44, '#FF45CF')
    gradient.addColorStop(0.83, '#00A5B9')
    gradient.addColorStop(1, '#FF45CF')

    playerRef.current = WaveSurfer.create({
      container: containerRef.current!,
      height: 25,
      waveColor: '#BDBDBD',
      progressColor: gradient,
      barWidth: 1,
      barGap: 2,
      barRadius: 2,
      cursorWidth: 0,
      normalize: true,
      hideScrollbar: true,
    });

    playerRef.current.load(url);

    //Event listeners for player
    playerRef.current.on('ready', () => {
      setPlayerState((state) => {
        return {
          ...state,
          duration: Math.round(playerRef.current?.getDuration() || 0),
          waveformLoaded: true
        }
      });

      playerRef.current?.setTime(0);
    });

    playerRef.current.on('play', () => {
      setPlayerState((state) => {
        return {
          ...state,
          audioPlaying: playerRef.current?.isPlaying() || false,
        }
      });
    });

    playerRef.current.on('pause', () => {
      setPlayerState((state) => {
        return {
          ...state,
          audioPlaying: playerRef.current?.isPlaying() || false,
        }
      });
    });

    playerRef.current.on('audioprocess', () => {
      setPlayerState((state) => {
        return {
          ...state,
          currentTime: Math.round(playerRef.current?.getCurrentTime() || 0)
        }
      });
    });

    playerRef.current.on('seeking', () => {
      setPlayerState((state) => {
        return {
          ...state,
          currentTime: playerRef.current?.getCurrentTime() || 0,
        }
      });
    });

    playerRef.current.on('finish', () => {
      setPlayerState((state) => {
        return {
          ...state,
          audioPlaying: playerRef.current?.isPlaying() || false,
          currentTime: 0
        }
      });
    });

    playerRef.current.on('error', (e) => {
      setPlayerState((state) => {
        return {
          ...state,
          error: e
        }
      })
    });
  };

  const onPlayPause = useCallback(() => {
    if (playerRef.current) {
      playerRef.current.playPause();
    }
  }, [playerRef.current])

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    const paddedMins = String(mins).padStart(2, '0');
    const paddedSecs = String(secs).padStart(2, '0');

    if (hrs === 0) {
      return `${paddedMins}:${paddedSecs}`;
    } else {
      const paddedHrs = String(hrs);
      return `${paddedHrs}:${paddedMins}:${paddedSecs}`;
    }
  }

  const handleCollapse = () => {
    simulationContext.changeSecondaryView?.("call-recording-minimized");
  }

  const handleSkipBackward = useCallback(() => {
    if (playerRef.current) {
      const newTime = playerRef.current.getCurrentTime() - 15; // Adjust this value as needed
      playerRef.current.seekTo(newTime / playerRef.current.getDuration());
    }
  }, [playerRef.current]);

  const handleSkipForward = useCallback(() => {
    if (playerRef.current) {
      const newTime = playerRef.current.getCurrentTime() + 30; // Adjust this value as needed
      playerRef.current.seekTo(newTime / playerRef.current.getDuration());
    }
  }, [playerRef.current]);


  return (
    <div className={cx(styles.callRecordingPlayer, {
      [styles.minimized]: minimized,
    })}>
      {loadingTransition((style, item) => item && (
        <animated.div className={styles.loading} style={style}>
          <AudioVisualizerLoader />
        </animated.div>
      ))}

      <animated.div className={styles.player} style={playerStyles}>
        <div className={styles.track}>
          <div className={styles.actions}>
            {minimized &&
              <button onClick={handleSkipBackward} className={cx(styles.action, styles.rewind)}>
                <i className="paperflite-reload"></i>
                <span>15</span>
              </button>
            }
            <button onClick={onPlayPause} className={cx(styles.action, styles.play)}>
              <i className={playerState.audioPlaying ? "paperflite-pause" : "paperflite-play"}></i>
            </button>
            {minimized &&
              <button onClick={handleSkipForward} className={cx(styles.action, styles.forward)}>
                <i className="paperflite-reload"></i>
                <span>30</span>
              </button>
            }
          </div>

          <div ref={containerRef} className={styles.waveform} />
          <div className={styles.time}>{formatTime(playerState.currentTime || 0)} / {formatTime(playerState.duration || 0)}</div>
        </div>

        {!minimized && (
          <Tooltip content={"Collapse player"} placement="top">
            <button onClick={handleCollapse} className={cx(styles.action, styles.collapse)}>
              <i className={"paperflite-collapse"}></i>
            </button>
          </Tooltip>
        )}
      </animated.div>
    </div>
  );
}

const AudioVisualizerLoader: React.FC = () => {
  return (
    <div className={styles.audioVisualizerLoader}>
      <SoundVisualizer
        disabled={false}
        volumeLevel={1}
        barCount={120}
        barWidth="2px"
        height={30}
        color="#FF45C2"
        durationInSeconds={0.25}
        cornerRadius={8}
        verticalAlignment="center"
      />
      <div className={styles.message}>Analyzing call recording...</div>
    </div>
  );
}